/** SRS #39 — first-time guided tour: Search → Charts → Digest → Chat.
 *
 * Steps are keyed by workspace; each names the route the step lives on and
 * a `data-tour` anchor. A step whose anchor is missing (plan-locked page,
 * collapsed sidebar) is skipped rather than blocking the tour. */

export type Workspace = 'app' | 'enterprise' | 'startup' | 'government';

export interface TourStep {
  id: 'search' | 'charts' | 'digest' | 'chat';
  route: string;
  anchor: string;          // value of data-tour on the element to spotlight
  title: string;
  body: string;
  /** Selector for a fallback anchor when the primary is not rendered. */
  fallback?: string;
}

const COPY = {
  search: { title: 'Search', body: 'Find regulations by topic, bill name or plain language. Filter by jurisdiction, type, status and date — results combine keyword and semantic matching.' },
  charts: { title: 'Charts', body: 'See where activity is rising or slowing: regulations by jurisdiction, status mix and trends over time. Use the ranges to support reporting.' },
  digest: { title: 'Digest', body: 'The AI-policy newsletter, curated by source and topic. Pick a daily, weekly or monthly digest and personalise it to your countries and topics.' },
  chat: { title: 'Chat', body: 'Ask the assistant about any regulation in the corpus. Answers cite their sources — follow them to the original text.' },
};

export function workspaceFor(pathname: string): Workspace {
  if (pathname.startsWith('/enterprise/')) return 'enterprise';
  if (pathname.startsWith('/startup/')) return 'startup';
  if (pathname.startsWith('/government/')) return 'government';
  return 'app';
}

export function tourSteps(workspace: Workspace): TourStep[] {
  if (workspace !== 'app') {
    const b = `/${workspace}/dashboard`;
    return [
      { id: 'search', route: b, anchor: 'nav-search', ...COPY.search },
      { id: 'charts', route: b, anchor: 'nav-charts', ...COPY.charts },
      { id: 'digest', route: b, anchor: 'nav-newsletters', ...COPY.digest },
      { id: 'chat', route: b, anchor: 'nav-chat', ...COPY.chat },
    ];
  }
  return [
    { id: 'search', route: '/dashboard', anchor: 'quick-search', fallback: '[data-tour="app-search"]', ...COPY.search },
    { id: 'charts', route: '/dashboard', anchor: 'tab-chart', ...COPY.charts },
    { id: 'digest', route: '/dashboard', anchor: 'tab-newsletters', ...COPY.digest },
    { id: 'chat', route: '/dashboard', anchor: 'quick-chat', fallback: '[data-tour="app-chat"]', ...COPY.chat },
  ];
}

/** Where the tour should auto-start: any dashboard home. */
export function isTourHome(pathname: string): boolean {
  return /^\/(dashboard|enterprise\/dashboard|startup\/dashboard|government\/dashboard)\/?$/.test(pathname);
}
